// @flow

import DOM from '../../util/dom';
import {extend, bindAll} from '../../util/util';
import Marker from '../marker';
import { RoutingService } from '../../services/RoutingService';
import polyline from '@mapbox/polyline';
import { ACTION } from '../../constants/road-draw-history-action';
import type {MapMouseEvent} from '../events';
import LngLat from '../../geo/lng_lat';
import type {LngLatLike} from '../../geo/lng_lat';
import {Event, Evented} from '../../util/evented';
import turf from '@turf/turf';
import type Map from '../map';
import Point from '@mapbox/point-geometry';

type Listener = (Object) => any;
type Listeners = { [string]: Array<Listener> };

type History = {
    action: string,
    index: number,
    currentLngLat: LngLat
};

type Options = {
    accessToken: string,
    mode?: string,
    addable: boolean,
};

type MarkerAlongPath = {
    index: number,
}

const defaultOptions: Options = {
    mode: 'driving',
    addable: true
};

/**
 * A `RoadDrawerControl` control contains zoom buttons and a compass.
 *
 * @implements {IControl}
 * @param {Object} [options]
 * @example
 * var nav = new mapboxgl.RoadDrawerControl({
 *  accessToken: 'ACCESS_TOKEN'
 * });
 * map.addControl(nav, 'top-left');
 * @see [Display map navigation controls](https://www.mapbox.com/mapbox-gl-js/example/navigation/)
 * @see [Add a third party vector tile source](https://www.mapbox.com/mapbox-gl-js/example/third-party/)
 */
class RoadDrawerControl extends Evented {
    _map: Map;
    options: Options;
    _container: HTMLElement;
    _markerSvg: string;
    _listMarkers: Marker[];
    _routingService: RoutingService;
    _infoPanel: HTMLElement;
    _loading: boolean;
    _undoBtn: HTMLElement;
    _refreshBtn: HTMLElement;
    _loadingEl: HTMLElement;
    _totalDistanceEl: HTMLElement;
    _closeBtn: HTMLElement;
    _histories: History[];
    _endDraw: boolean;
    SOURCE_ID: string;
    LAYER_ID: string;
    DATA_SOURCE: GeoJSON;
    _markerIndexAlongPath: MarkerAlongPath[];
    LNGLAT_PRECISION: number;
    activeState: boolean;
    _markerEventListener: Listeners[];

    constructor(options: Options) {
        super();
        this.options = extend({}, defaultOptions, options);

        this._container = DOM.create('div', 'vtmapgl-ctrl vtmapgl-ctrl-group');
        this._container.addEventListener('contextmenu', (e) => e.preventDefault());
        this._markerSvg = '<svg display="block" height="41px" width="27px" viewBox="0 0 27 41"><g fill-rule="nonzero"><g transform="translate(3.0, 29.0)" fill="#000000"><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="10.5" ry="5.25002273"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="10.5" ry="5.25002273"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="9.5" ry="4.77275007"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="8.5" ry="4.29549936"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="7.5" ry="3.81822308"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="6.5" ry="3.34094679"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="5.5" ry="2.86367051"></ellipse><ellipse opacity="0.04" cx="10.5" cy="5.80029008" rx="4.5" ry="2.38636864"></ellipse></g><g fill="#3fb1ce"><path d="M27,13.5 C27,19.074644 20.250001,27.000002 14.75,34.500002 C14.016665,35.500004 12.983335,35.500004 12.25,34.500002 C6.7499993,27.000002 0,19.222562 0,13.5 C0,6.0441559 6.0441559,0 13.5,0 C20.955844,0 27,6.0441559 27,13.5 Z"></path></g><g opacity="0.25" fill="#000000"><path d="M13.5,0 C6.0441559,0 0,6.0441559 0,13.5 C0,19.222562 6.7499993,27 12.25,34.5 C13,35.522727 14.016664,35.500004 14.75,34.5 C20.250001,27 27,19.074644 27,13.5 C27,6.0441559 20.955844,0 13.5,0 Z M13.5,1 C20.415404,1 26,6.584596 26,13.5 C26,15.898657 24.495584,19.181431 22.220703,22.738281 C19.945823,26.295132 16.705119,30.142167 13.943359,33.908203 C13.743445,34.180814 13.612715,34.322738 13.5,34.441406 C13.387285,34.322738 13.256555,34.180814 13.056641,33.908203 C10.284481,30.127985 7.4148684,26.314159 5.015625,22.773438 C2.6163816,19.232715 1,15.953538 1,13.5 C1,6.584596 6.584596,1 13.5,1 Z"></path></g><g transform="translate(6.0, 7.0)" fill="#FFFFFF"></g><g transform="translate(8.0, 8.0)"><circle fill="#000000" opacity="0.25" cx="5.5" cy="5.5" r="5.4999962"></circle><circle fill="#FFFFFF" cx="5.5" cy="5.5" r="5.4999962"></circle></g></g></svg>';
        this._listMarkers = [];
        this._routingService = new RoutingService({ accessToken: this.options.accessToken });
        this._loading = false;
        this._histories = [];
        this._endDraw = false;
        this.SOURCE_ID = 'roaddraw-source';
        this.LAYER_ID = 'roaddraw-layer';
        this._markerIndexAlongPath = [];
        this.LNGLAT_PRECISION = 5;
        this.activeState = true;

        bindAll([
            '_handleClickMap',
            '_onMarkerClick',
            '_handleClickUndo',
            '_handleClickRefresh',
            '_onMarkerDrag',
            '_finish',
            '_handleEndDraw',
            '_keydownListener',
            '_showPointer',
            '_hidePointer',
            '_isClickOnRoute',
            '_handleClickClose',
        ], this);

        this._infoPanel = this._createInfoPanel();
    }

    _handleClickMap(e: MapMouseEvent) {
        // Check if click on route
        if(this._isClickOnRoute(e.point)) {
            this.fire('click', e);
            return;
        }
        if (this._loading || this._endDraw || !this.activeState || !this.options.addable) {
            return;
        }
        this._loading = true;
        setTimeout(() => {
            this._addMarker(e.lngLat);
            this._updatePath();
        }, 500);
    }

    _isClickOnRoute(point: Point) {
        const width = 10;
        const height = 20;
        const features = this._map.queryRenderedFeatures([
            [point.x - width / 2, point.y - height / 2],
            [point.x + width / 2, point.y + height / 2]
        ], {
            layers: [this.LAYER_ID]
        });
        return features.length > 0;
    }

    _showLoading() {
        this._loading = true;
        this._loadingEl.style.display = 'inline-block';
    }

    _hideLoading() {
        this._loading = false;
        this._loadingEl.style.display = 'none';
    }

    _finish(e: Point) {
        if (this._isClickOnRoute(e.point)) {
            return;
        }
        if (this._endDraw) {
            return;
        }
        setTimeout(() => {
            this._endDraw = true;
        }, 300);
    }

    onAdd(map: Map) {
        this._map = map;
        this._map.doubleClickZoom.disable();
        this._map.on('click', this._handleClickMap);
        this._map.on('dblclick', this._finish);

        document.addEventListener('keydown', this._keydownListener);

        DOM.addEventListener(this._undoBtn, 'click', this._handleClickUndo);
        DOM.addEventListener(this._refreshBtn, 'click', this._handleClickRefresh);
        DOM.addEventListener(this._closeBtn, 'click', this._handleClickClose);

        if (this._map.loaded()) this._addSource()
        else this._map.on('load', () => this._addSource());

        this._map.on('styledata', () => {
            this._addSource()
            this._updatePath();
        });

        return this._container;
    }

    onRemove() {
        DOM.remove(this._container);
        this._map.off('click', this._handleClickMap);
        this._map.off('dblclick', this._finish);

        this._map.off('mouseenter', this.LAYER_ID, this._showPointer);
        this._map.off('mouseleave', this.LAYER_ID, this._hidePointer);

        document.removeEventListener('keydown', this._keydownListener);

        DOM.removeEventListener(this._undoBtn, 'click', this._handleClickUndo);
        DOM.removeEventListener(this._refreshBtn, 'click', this._handleClickRefresh);
        DOM.removeEventListener(this._closeBtn, 'click', this._handleClickClose);

        this._clearMarkers();
        // this._clearPath();
        this._updateTotalDistance(0);

        delete this._map;
    }

    _updateSource(path: LngLatLike[]) {
        this.DATA_SOURCE.geometry.coordinates = path;
        this._map.getSource(this.SOURCE_ID).setData(this.DATA_SOURCE);
    }

    _keydownListener(e) {
        if (this._loading || this._endDraw) return;
        // Ctrl + Z
        if (e.keyCode == 90 && e.ctrlKey) {
            this._handleClickUndo();
        }
    }

    _handleClickUndo() {
        if (this._loading || this._endDraw || !this.activeState) return;
        const history = this._histories.pop();
        if (!history) return;

        switch (history.action) {
            case ACTION.ADD:
                const marker = this._listMarkers.pop();
                marker.remove();
                break;

            case ACTION.REMOVE:
                this._addMarker(history.currentLngLat, history.index, false);
                break;

            case ACTION.MOVE:
                this._listMarkers[history.index].setLngLat(history.currentLngLat);
                break;

            default:
                break;
        }

        this._updatePath();
    }

    _handleClickRefresh() {
        if (this._loading || !this.activeState) return;
        this.refresh();
        if (this._endDraw) {
            this._map.doubleClickZoom.disable();
            this._endDraw = false;
        }
    }

    refresh() {
        this._clearPath();
        this._clearMarkers();
        this._histories = [];
    }

    _handleClickClose() {
        const confirm = window.confirm('Bạn có chắc chắn muốn kết thúc vẽ tuyến không?');
        if (confirm) {
            this._map.removeControl(this);
        }
    }

    _createInfoPanel() {
        const infoPanel = DOM.create('div', 'road-draw-info-panel', this._container);
        infoPanel.innerHTML = `
        <div class='road-draw-header'>
            <span>Thông tin tuyến vẽ</span>
        </div>
        <div class='road-draw-body'>
            <div class='road-draw-row' id='road-draw-error-wrap'>
                <span id='road-draw-error'>Lỗi trong quá trình xử lý</span>
            </div>
        </div>`

        // Close button
        const closeBtn = DOM.create('button', 'close');
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.innerHTML = '<span aria-hidden="true">&times;</span>';
        this._closeBtn = closeBtn;

        // Total distance group
        const totalDistanceRow = DOM.create('div', 'road-draw-row', infoPanel);
        totalDistanceRow.innerHTML = `<span class='road-draw-distance-title'>Tổng khoảng cách:</span>`;
        const totalDistanceEl = DOM.create('span', '', totalDistanceRow);
        totalDistanceEl.id = 'road-draw-total-distance';
        totalDistanceEl.innerText = '0m';
        this._totalDistanceEl = totalDistanceEl;

        // Create btn group
        const btnRow = DOM.create('div', 'road-draw-row', infoPanel);
        const undoBtn = DOM.create('div', 'road-draw-btn', btnRow);
        undoBtn.id = 'road-draw-undo-btn';
        const refreshBtn = DOM.create('div', 'road-draw-btn', btnRow);
        refreshBtn.id = 'road-draw-refresh-btn';
        const loadingEl = DOM.create('div', 'road-draw-loading', btnRow);
        loadingEl.id = 'road-draw-loading';
        loadingEl.style.display = 'none';
        this._undoBtn = undoBtn;
        this._refreshBtn = refreshBtn;
        this._loadingEl = loadingEl;

        infoPanel.getElementsByClassName('road-draw-header')[0].append(closeBtn);
        infoPanel.getElementsByClassName('road-draw-body')[0].prepend(totalDistanceRow);
        infoPanel.getElementsByClassName('road-draw-body')[0].append(btnRow);

        return infoPanel;
    }

    _addMarker(lngLat: LngLat, index: number = this._listMarkers.length, createHistory: boolean = true) {
        // Create marker
        const markerEl = DOM.create('div', 'indexed-marker');

        const iconEl = DOM.create('span');
        iconEl.innerHTML = this._markerSvg;

        const indexEl = DOM.create('span', 'indexed-marker-number');
        indexEl.innerHTML = index;

        markerEl.append(indexEl)
        markerEl.append(iconEl);

        // Add to map
        const marker = new Marker({
            element: markerEl,
            anchor: 'bottom',
            offset: [0, 6],
            draggable: true
        }).setLngLat(lngLat).addTo(this._map);

        // Add to list
        this._listMarkers.splice(index, 0, marker);
        // Update index
        if (index < this._listMarkers.length - 1) {
            for (let i = index + 1; i < this._listMarkers.length; i++) {
                // const indexEl = this._listMarkers[i].getElement().getElementsByClassName('indexed-marker-number')[0];
                // indexEl.textContent = i;
                this._setMarkerIndex(this._listMarkers[i], i);
            }
        }

        // ADD history
        if (createHistory) {
            this._histories.push({
                action: ACTION.ADD
            });
        }

        // Add events
        const clickHandler = e => this._onMarkerClick(e, marker);
        
        let intialLngLat;
        const dragStartHandler = () => {
            intialLngLat = new LngLat(marker.getLngLat().lng, marker.getLngLat().lat);
        }
        const dragEndHandler = () => this._onMarkerDrag(index, intialLngLat);

        markerEl.addEventListener('click', clickHandler);
        marker.on('dragstart', dragStartHandler);
        marker.on('dragend', dragEndHandler);

        // this._markerEventListener[index] = {
        //     'click': [clickHandler],
        //     'dragstart': [dragStartHandler],
        //     'dragend': [dragEndHandler]
        // };

        // this._markerEventListener[marker]['click'].push(clickHandler);
        // this._markerEventListener[marker]['dragstart'].push(dragStartHandler);
        // this._markerEventListener[marker]['dragend'].push(dragEndHandler);
    }

    _enableMarkerDrag() {
        this._listMarkers.forEach(m => m.setDraggable(true));
    }
    _disableMarkerDrag() {
        this._listMarkers.forEach(m => m.setDraggable(false));
    }

    // _removeMarkerEvent(index: number, event: string) {
    //     const marker = this._listMarkers[index];
    //     function removeClickEvent(listener) {
    //         marker.getElement().removeEventListener(event, listener);
    //     }
    //     function removeOtherEvent(listener) {
    //         marker.off(event, listener);
    //     }
    //     if (marker && this._markerEventListener[index] && this._markerEventListener[index][event]) {
    //         if (event === 'click') {
    //             this._markerEventListener[index][event].forEach(removeClickEvent);
    //         } else {
    //             this._markerEventListener[index][event].forEach(removeOtherEvent);
    //         }
    //     }
    // }

    // _removeMarkerEvents(index: number, ...events: string[]) {
    //     events.forEach(e => this._removeMarkerEvent(index, e));
    // }

    // _enableMarkerEvent(index: number, event: string) {
    //     const marker = this._listMarkers[index];
    //     function enableClickEvent(listener) {
    //         marker.getElement().addEventListener(event, listener);
    //     }
    //     function enableOtherEvent(listener) {
    //         marker.on(event, listener);
    //     }
    //     if (marker && this._markerEventListener[index] && this._markerEventListener[index][event]) {
    //         if (event === 'click') {
    //             this._markerEventListener[index][event].forEach(enableClickEvent);
    //         } else {
    //             this._markerEventListener[index][event].forEach(enableOtherEvent);
    //         }
    //     }
    // }

    // _enableMarkerEvents(index: number, ...events: string[]) {
    //     events.forEach(e => this._enableMarkerEvent(index, e));
    // }
    
    _onMarkerDrag(index: number, fromLngLat: LngLat) {
        // MOVE history
        this._histories.push({
            action: ACTION.MOVE,
            index,
            currentLngLat: fromLngLat
        });
        this._updatePath();
    }

    _clearPath() {
        this._updateSource([]);
        this._updateTotalDistance(0);
    }

    _clearMarkers(clearData: boolean = true) {
        this._listMarkers.forEach(m => {
            m.remove();
            m = null;
        });
        if (clearData) {
            this._listMarkers = [];
        }
    }

    _isEqualLngLat(point1: LngLatLike, point2: LngLatLike) {
        const EPSILON = 0.00001;
        return Array.isArray(point1) && Array.isArray(point2) && Math.abs(point1[0] - point2[0]) < EPSILON && Math.abs(point1[1] - point2[1]) < EPSILON;
    }

    _updatePath() {
        this._showLoading();
        this._disableMarkerDrag();
        if (this._listMarkers.length < 2) {
            this._clearPath();
            this._hideLoading();
            this._enableMarkerDrag();
            return;
        }
        const queryObj = this._buildDirectionQuery();
        this._routingService.fetchDirectionAsync(queryObj)
        .then(res => res.json())
        .then(json => {
            this._markerIndexAlongPath = [];

            const decodedPaths = polyline.decode(json.routes[0].geometry, this.LNGLAT_PRECISION).map(c => c.reverse());
            const waypoints = json.waypoints;
            waypoints.forEach((waypoint, currentMarkerIndex) => {
                let location = this._roundLngLat(waypoint.location);
                let pathIndex = decodedPaths.findIndex(pathCoord => this._isEqualLngLat(pathCoord, location));
                if (pathIndex === -1) {
                    // Set to last point on the path if compare not match
                    // Hope this work
                    pathIndex = decodedPaths.length - 1;
                    location = decodedPaths[pathIndex];
                }
                this._updateLngLatMarkerAndHistory(currentMarkerIndex, location);
                this._markerIndexAlongPath.push({
                    index: pathIndex
                });
            });

            this._updateSource(decodedPaths);

            // Update panel info
            this._updateInfoPanel(json);

            this._hideLoading();

            if (this._endDraw) {
                this._handleEndDraw();
            } else if (this.activeState) {
                this._enableMarkerDrag();
            }
        });
    }

    _roundLngLat(lngLat: LngLatLike) {
        return [this._roundNumberToFixed(lngLat[0]), this._roundNumberToFixed(lngLat[1])];
    }

    _roundNumberToFixed(num: number) {
        let numStr = parseFloat(num).toFixed(this.LNGLAT_PRECISION + 1);
        // Check last number >= 5 to round up
        numStr = numStr.replace(/5$/, 9);
        return parseFloat(parseFloat(numStr).toFixed(this.LNGLAT_PRECISION));
    }

    _updateLngLatMarkerAndHistory(index: number, newLngLat: LngLatLike) {
        const oldMarkerLngLat = this._listMarkers[index].getLngLat();
        this._listMarkers[index].setLngLat(newLngLat);
        // Update history
        this._histories = this._histories.map(h => {
            if (h.currentLngLat && h.currentLngLat.toString() === oldMarkerLngLat.toString()) {
                h.currentLngLat = this._listMarkers[index].getLngLat();
            }
            return h;
        });
    }

    _handleEndDraw() {
        this._disableMarkerDrag();
        this._histories = [];
        this._map.doubleClickZoom.enable();
        this._clearMarkers(false);
        this.fire(new Event('endDraw'));
    }

    _updateInfoPanel(directionResponse) {
        // Update total distance
        const totalDistance = directionResponse.routes[0].distance;
        this._updateTotalDistance(totalDistance);
    }

    _updateTotalDistance(totalDistance: number) {
        this._totalDistanceEl.innerText = Math.round(totalDistance) + 'm';
    }

    _buildDirectionQuery() {
        const coordinates = this._listMarkers.map(m => m.getLngLat().toArray().join(',')).join(';');
        return { coordinates, mode: this.options.mode };
    }

    _onMarkerClick(e: MapMouseEvent, marker: Marker) {
        e.preventDefault();
        e.stopPropagation();
        if (!this.activeState) return;
        // Update index list markers
        let removedIndex = -1;
        this._listMarkers = this._listMarkers.reduce((acc: Marker[], currentMarker: Marker, currentIndex: number) => {
            if (currentMarker === marker) {
                removedIndex = currentIndex;
            } else {
                if (removedIndex > -1 && currentIndex > removedIndex) {
                    // Update index of all markers align after removed index
                    this._setMarkerIndex(currentMarker, currentIndex -1);
                }
                acc.push(currentMarker);
            }

            return acc;
        }, [])
        this._updatePath();
        marker.remove();
        // REMOVE history
        this._histories.push({
            action: ACTION.REMOVE,
            index: removedIndex,
            currentLngLat: new LngLat(marker.getLngLat().lng, marker.getLngLat().lat)
        });
    }

    _setMarkerIndex(marker: Marker, index: number) {
        marker.getElement().firstElementChild.innerHTML = index;
    }

    _addSource() {
        this.DATA_SOURCE = {
            type: 'Feature',
            geometry: {
                type: 'LineString',
                coordinates: []
            }
        };
        // Add and set data theme layer/style
        if (!this._map.getSource(this.SOURCE_ID)) {
            this._map.addSource(this.SOURCE_ID, {
                type: 'geojson',
                data: this.DATA_SOURCE
            });
        }
        if (!this._map.getLayer(this.LAYER_ID)) {
            this._map.addLayer({
                id: this.LAYER_ID,
                type: 'line',
                source: this.SOURCE_ID,
                layout: {
                },
                paint: {
                    'line-color': '#ff9900',
                    'line-width': 4
                }
            });
            this._map.on('mouseenter', this.LAYER_ID, this._showPointer);
            // Change it back to a pointer when it leaves.
            this._map.on('mouseleave', this.LAYER_ID, this._hidePointer);
        }
    }
    _showPointer() {
        this._map.getCanvas().style.cursor = 'pointer';
    }
    _hidePointer() {
        this._map.getCanvas().style.cursor = '';
    }

    /**
     * Return array of lnglat: [[lng,lat], [lng,lat]]
     */
    getPoints() {
        return JSON.parse(JSON.stringify(this.DATA_SOURCE.geometry.coordinates));
    }

    /**
     * Get index of marker along path
     */
    getViaMarkerData() {
        return JSON.parse(JSON.stringify(this._markerIndexAlongPath));
    }

    /**
     * Set points from array of lnglat: [[lng,lat], [lng,lat]]
     */
    /**
     *
     * @param {*} points [[lng,lat], [lng,lat]...]
     */
    setPoints(points: LngLatLike[]) {
        this.refresh();
        points.forEach(lngLat => {
            this._addMarker(LngLat.convert(lngLat));
        });
        this._updatePath();
    }

    setRoad(points: LngLatLike[], viaMarkerData: MarkerAlongPath[]) {
        this.refresh();
        if (!Array.isArray(viaMarkerData)) {
            viaMarkerData = [
                { index: 0 },
                { index: points.length - 1 }
            ]
        }
        viaMarkerData.forEach(alongPath => {
            this._addMarker(LngLat.convert(points[alongPath.index]));
        });
        this._updateSource(points);
        const directionResponse = {
            routes: [{
                distance: turf.length(turf.lineString(points), { units: 'meters' })
            }]
        }
        this._updateInfoPanel(directionResponse);
    }

    deactive() {
        this.activeState = false;
        this._disableMarkerDrag();
    }

    active() {
        this.activeState = true;
        this._enableMarkerDrag();
    }

    setAddable(shouldBeAddable: boolean) {
        this.options.addable = !!shouldBeAddable;
    }
}

export default RoadDrawerControl;
